<template>
  <div class="resource-list">
    <div 
      v-for="(resource, index) in sortedResources" 
      :key="resource.id" 
      class="resource-card"
      :style="{ animationDelay: `${index * 0.05}s` }"
    >
      <!-- Card Header -->
      <div class="card-header">
        <div class="card-icon" :class="{ 'has-image': resource.isImage }">
          <img 
            v-if="resource.isImage" 
            :src="require('@/assets/img/' + resource.icon.split('/').pop())" 
            :alt="resource.title"
            class="icon-image"
          >
          <span v-else class="icon-emoji">{{ resource.icon }}</span>
        </div>
        <div class="card-meta">
          <h3 class="card-title">{{ resource.title }}</h3>
          <span class="card-type">{{ resource.type }}</span>
        </div>
      </div>
      
      <!-- Card Description -->
      <p class="card-desc">{{ resource.description }}</p>
      
      <!-- Card Action -->
      <button 
        class="card-btn"
        @click="openUrl(resource.links.primary.url)"
      >
        <svg class="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>
        </svg>
        <span>{{ resource.links.primary.text }}</span>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ResourceList',
  props: {
    resources: {
      type: Array,
      required: true
    }
  },
  computed: {
    sortedResources() {
      return [...this.resources].sort((a, b) => (a.sortOrder || 999) - (b.sortOrder || 999));
    }
  },
  methods: {
    openUrl(url) {
      if (url) {
        window.open(url, '_blank');
      }
    }
  }
};
</script>

<style scoped>
.resource-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Card */
.resource-card {
  background: linear-gradient(145deg, #ffffff 0%, #f8fcfd 100%);
  border-radius: 16px;
  padding: 16px;
  box-shadow: 
    0 1px 3px rgba(8, 145, 178, 0.08),
    0 4px 12px rgba(8, 145, 178, 0.04);
  border: 1px solid rgba(8, 145, 178, 0.08);
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: default;
  animation: slideIn 0.4s ease-out backwards;
}

.resource-card:hover {
  transform: translateY(-2px);
  box-shadow: 
    0 4px 12px rgba(8, 145, 178, 0.12),
    0 8px 24px rgba(8, 145, 178, 0.08);
  border-color: rgba(8, 145, 178, 0.15);
}

.resource-card:active {
  transform: translateY(0);
}

/* Card Header */
.card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
}

.card-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #E0F7FA 0%, #B2EBF2 100%);
  flex-shrink: 0;
  transition: transform 0.2s ease;
}

.resource-card:hover .card-icon {
  transform: scale(1.05);
}

.card-icon.has-image {
  background: linear-gradient(135deg, #F0FDFA 0%, #CCFBF1 100%);
}

.icon-image {
  width: 32px;
  height: 32px;
  object-fit: contain;
  border-radius: 8px;
}

.icon-emoji {
  font-size: 22px;
  line-height: 1;
}

.card-meta {
  flex: 1;
  min-width: 0;
}

.card-title {
  font-size: 15px;
  font-weight: 600;
  color: #164E63;
  margin: 0 0 4px 0;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.card-type {
  display: inline-block;
  font-size: 11px;
  font-weight: 500;
  color: #0891B2;
  background: rgba(8, 145, 178, 0.1);
  padding: 3px 8px;
  border-radius: 6px;
  letter-spacing: 0.3px;
}

/* Card Description */
.card-desc {
  font-size: 13px;
  color: #64748B;
  line-height: 1.5;
  margin: 0 0 14px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Card Button */
.card-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  background: linear-gradient(135deg, #0891B2 0%, #0E7490 100%);
  color: white;
  box-shadow: 0 2px 8px rgba(8, 145, 178, 0.25);
}

.card-btn:hover {
  background: linear-gradient(135deg, #0E7490 0%, #155E75 100%);
  box-shadow: 0 4px 12px rgba(8, 145, 178, 0.35);
  transform: translateY(-1px);
}

.card-btn:active {
  transform: translateY(0);
  box-shadow: 0 1px 4px rgba(8, 145, 178, 0.2);
}

.btn-icon {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
}

/* Animations */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive */
@media (max-width: 375px) {
  .resource-card {
    padding: 14px;
  }
  
  .card-icon {
    width: 44px;
    height: 44px;
  }
  
  .icon-image {
    width: 28px;
    height: 28px;
  }
  
  .card-title {
    font-size: 14px;
  }
  
  .card-desc {
    font-size: 12px;
  }
  
  .card-btn {
    padding: 10px 14px;
    font-size: 13px;
  }
}
</style>
